import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getPublicBuildingListAsync } from "src/api/building/building-api";
import { getContractsAsync, getMemberContractsAsync } from "src/api/contract/contract-api";
import { MemberContractModel, changeStatusText } from "src/api/contract/contract-types";
import { getPublicMediaFilesAsync } from "src/api/file/file-api";
import { MediaFile } from "src/api/file/file-types";
import { MediaServiceType } from "src/api/public-types";
import { useApiOperation } from "src/api/hooks";
import { usePartnerNavigate } from "src/api/hooks/usePartnerNavigate";
import { MbMemberState } from "src/api/member/member-types";
import BottomLinkList from "src/components/BottomLinkList";
import Footer from "src/components/layout/Footer";
import Header from "src/components/layout/Header";
import MetaTag from "src/components/layout/MetaTag";
import { useMemberInfoState } from "src/recoil/member/hook";
import { formatDateTimeRange, getResizedImageUrl } from "src/utils/common-util";
import getAppInfo, { getMypageTitle } from "src/utils/app-Info";
import ContractUseProgressProductView from "./components/ContractUseProgressProductView";
import MyPageVisitorApproval from "./components/MyPageVisitorApproval";

// 타입 정의
interface ViewProductData {
  id: string;
  productName: string;
  buildingName: string;
  address: string;
  contractName: string;
  src?: string; // 공간상품 대표 이미지 URL
}

/**
 * 마이페이지 메인 화면
 */
const MyPageMain = () => {
  const initializedRef = useRef(false);

  const navigate = usePartnerNavigate();

  // 로그인한 회원 정보
  const { member } = useMemberInfoState();

  // PS-1811: 마이 > '이용 가능한 공간' 섹션은 Taap/IFC에서 숨김 (재노출 시 이 조건만 되돌린다)
  const isAvailableSpaceSectionVisible = getAppInfo().appType === "STPM";

  // (무인증) 공통 미디어파일 목록 api
  const { executeAsync: getPublicMediaFiles } = useApiOperation(getPublicMediaFilesAsync, { noAuthenticationRequired: true, noHandleError: true });

  // 신청/계약 목록 조회 api
  const { executeAsync: getMemberContracts } = useApiOperation(getMemberContractsAsync);
  const { executeAsync: getContracts } = useApiOperation(getContractsAsync);
  const { executeAsync: getPublicBuildingList } = useApiOperation(getPublicBuildingListAsync);

  // 신청/계약 목록
  const [contracts, setContracts] = useState<Array<MemberContractModel>>([]);
  const [memberContracts, setMemberContracts] = useState<Array<MemberContractModel>>([]);

  const [viewProducts, setViewProducts] = useState<ViewProductData[]>([]);

  // 미디어파일 목록 조회 (serviceId 기준)
  const fetchMediaFiles = useCallback(
    async (serviceIds: string[], mediaServiceType: MediaServiceType): Promise<MediaFile[]> => {
      if (serviceIds.length === 0) return [];
      try {
        const { data } = await getPublicMediaFiles({
          serviceId: serviceIds.join(","),
          mediaServiceTypes: [mediaServiceType],
        });
        return data?.data?.content || [];
      } catch (error) {
        console.error("Failed to fetch media files:", error);
        return [];
      }
    },
    [getPublicMediaFiles],
  );

  // 이용승인, 이용중, 해지접수 계약만 필터하는 함수 (순수 필터)
  const filteredAvailableContracts = useCallback(
    (allContracts: MemberContractModel[]): MemberContractModel[] =>
      allContracts.filter((contract) => ["USE_APPROVAL", "USE_PROGRESS", "TERMINATE_RECEIVED"].includes(contract?.contractStep || "")),
    [],
  );

  // 이용 가능한 계약 -> 화면 표시용 데이터(ViewProductData) 생성 함수
  const makeViewProducts = useCallback(
    async (availableContracts: MemberContractModel[]): Promise<ViewProductData[]> => {
      if (availableContracts.length === 0) return [];

      // 건물 정보(건물명/주소) 조회용 - primaryBuildingId
      const uniqueBuildingIds = [
        ...new Set(
          availableContracts
            .map(({ primaryBuildingId }) => primaryBuildingId)
            .filter((id) => !_.isNil(id))
            .map((id) => String(id)),
        ),
      ];

      if (uniqueBuildingIds.length === 0) return [];

      // 공간상품 대표 이미지 조회용 - spaceProductId
      const uniqueSpaceProductIds = [
        ...new Set(
          availableContracts
            .map(({ spaceProductId }) => spaceProductId)
            .filter((id) => !_.isNil(id))
            .map((id) => String(id)),
        ),
      ];

      try {
        const [buildingRes, buildingMediaFiles, productMediaFiles] = await Promise.all([
          getPublicBuildingList({
            id: uniqueBuildingIds.join(","),
            page: 0,
            size: uniqueBuildingIds.length,
          }),
          fetchMediaFiles(uniqueBuildingIds, "MEDIA_BUILDING"), // 건물 외관 (우선)
          fetchMediaFiles(uniqueSpaceProductIds, "MEDIA_PRODUCT_OFFICE"), // 공간상품 대표 이미지 (폴백)
        ]);
        const buildings = buildingRes?.data?.data?.content || [];

        return availableContracts.reduce<ViewProductData[]>((contractsWithImages, contract, currentIndex) => {
          const { contractId, contractName = "", spaceProductName = "", spaceProductId, primaryBuildingId } = contract || {};

          if (!primaryBuildingId) {
            return contractsWithImages;
          }

          // primaryBuildingId로 building 찾기 (건물명/주소용)
          const foundBuilding = buildings.find((building) => String(building.id) === String(primaryBuildingId));

          if (!foundBuilding) {
            return contractsWithImages;
          }

          const { id: buildingId, buildingName = "", address = "" } = foundBuilding;

          // 건물 대표 이미지 (우선): serviceId === buildingId 필터 -> isPrimary
          const buildingImageUrl = buildingMediaFiles
            .filter((file) => String(file.serviceId) === String(buildingId))
            .find((file) => file.isPrimary === true)?.url;

          // 공간상품 대표 이미지 (폴백): serviceId === spaceProductId 필터 -> isPrimary 우선 -> url
          const productImageUrl = productMediaFiles
            .filter((file) => String(file.serviceId) === String(spaceProductId))
            .sort((a, b) => (a.isPrimary ? -1 : b.isPrimary ? 1 : 0))[0]?.url;

          contractsWithImages.push({
            id: String(contractId) + String(buildingId) + currentIndex,
            productName: spaceProductName,
            buildingName,
            address,
            contractName,
            src: buildingImageUrl || productImageUrl || "",
          });
          return contractsWithImages;
        }, []);
      } catch (error) {
        console.error("Failed to process use progress contracts:", error);
        return [];
      }
    },
    [getPublicBuildingList, fetchMediaFiles],
  );

  // 신청/계약 목록 조회
  const fetchContracts = useCallback(
    async (memberNo: string) => {
      try {
        const { data } = await getContracts({
          memberNo,
        });

        if (data?.data?.content) {
          // 이용자 상태 "삭제"인 것 제외
          const filteredContracts = (data.data.content || []).filter((contract: MemberContractModel) => contract?.memberStatus !== "MEMBER_DELETE");

          // 계약자의 상태가 마스터이거나 계약자인 것만 조회
          const filteredAuthContracts = filteredContracts.filter(
            (contract: MemberContractModel) => 
              // (contract.isAdmin || contract.isContractor) && 
            contract?.contractStep !== "APPLY_CANCEL",
          );

          setContracts((current) => (
            [
              ...current, 
              ...filteredAuthContracts.filter(
                (contract: MemberContractModel) => (
                  contract?.contractId && 
                  !current.some((c) => c.contractId === contract.contractId)
                )
              )
            ]
          ));
        }
      } catch (error) {
        console.error("fetchContracts error", error);
      }

      if (!initializedRef.current) {
        initializedRef.current = true;
      }
    },
    [getContracts],
  );
  const fetchMemberContracts = useCallback(
    async (memberNo: string) => {
      try {
        const { data } = await getMemberContracts({
          memberNo,
        });

        if (data?.data?.content) {
          const filteredContracts = (data.data.content || []).filter((contract: MemberContractModel) => contract?.memberStatus !== "MEMBER_DELETE");
          const filteredMemberContracts = filteredContracts.filter((contract: MemberContractModel) => contract?.contractStep !== "APPLY_CANCEL");

          // 이용 가능한(이용승인/이용중/해지접수) 계약만 추출
          const availableContracts = filteredAvailableContracts(filteredMemberContracts);
          setMemberContracts(availableContracts);

          // 화면 표시용 데이터 생성 (섹션을 숨기는 앱에서는 건물/미디어 조회를 하지 않는다)
          if (isAvailableSpaceSectionVisible) {
            const primaryBuildings = await makeViewProducts(availableContracts);
            setViewProducts(primaryBuildings);
          }
        }
      } catch (error) {
        console.error("fetchContracts error", error);
      }

      if (!initializedRef.current) {
        initializedRef.current = true;
      }
    },
    [filteredAvailableContracts, makeViewProducts, getMemberContracts, isAvailableSpaceSectionVisible],
  );

  useEffect(() => {
    if (member && member.memberNo) {
      // 신청/계약 목록 조회
      fetchContracts(member.memberNo);
      fetchMemberContracts(member.memberNo);
    }
  }, [member]);

  // 회원 이메인 인증 여부
  const isEmailVerified = useMemo(() => {
    if (!member) return false;
    return !!(member?.states || []).find((state: MbMemberState) => state.status === "EMAIL_VERIFIED");
  }, [member]);

  // 방문자 승인 대상 계약: 신청/계약(contracts) + 이용 계약(memberContracts) 합집합 (contractId 기준 중복 제거)
  const visitorApprovalContracts = useMemo(() => {
    const merged = [...contracts, ...memberContracts];
    return merged.filter(
      (contract, index) => contract?.contractId && merged.findIndex((c) => c.contractId === contract.contractId) === index,
    );
  }, [contracts, memberContracts]);

  return (
    <>
      <MetaTag title={getMypageTitle()} />
      <Header />
      <div className="my-page">
        <article className="after-login-contents bottom-divider">
          <div className="flex-center">
            <div className="profile-img">
              <span>{member?.email && member?.email.charAt(0).toLocaleUpperCase()}</span>
            </div>
            <div className="profile-content">
              <p className={isEmailVerified ? "" : "invalid"}>{member?.email}</p>
              <button type="button" onClick={() => navigate("/court/mypage/profile")}>
                프로필 편집
              </button>
            </div>
          </div>
        </article>
        {isAvailableSpaceSectionVisible && <ContractUseProgressProductView viewData={viewProducts} />}

        <article className="after-login-contract">
          <div className="title-area">
            <span>신청/계약</span>
            {contracts.length > 0 && (
              <button type="button" onClick={() => navigate("/court/mypage/contracts")}>
                더보기
              </button>
            )}
          </div>
          {/* 신청/계약 목록 */}
          {initializedRef.current && (
            <div className={`contents-area ${contracts.length === 0 ? "no-list" : ""}`}>
              {contracts.length > 0 ? (
                contracts.map((contract: MemberContractModel, index: number) => (
                  <div key={index} className={`${contracts.length === 1 ? "w-100" : ""} contents-card`}>
                    <div className="contents-card__title-area">
                      <p>{contract.contractName ? `${contract.contractName}` : contract.spaceProductName}</p>
                      <div
                        className={`base-chip ${["USE_APPROVAL", "USE_PROGRESS", "TERMINATE_RECEIVED"].includes(contract?.contractStep || "") ? "" : "color-gray"
                          }`}
                      >
                        {changeStatusText(contract?.spaceProductType!, contract?.contractStep!)}
                      </div>
                    </div>
                    {/* F2 타입: 이용기간, 이용자 현황 표시 */}
                    {contract?.spaceProductType !== "TIME_COURT" && (
                      <>
                        <div className="contents-card__info-area">
                          <p>
                            {contract?.contractStartTime &&
                              contract?.contractEndTime &&
                              formatDateTimeRange(contract?.contractStartTime, contract?.contractEndTime, "F2")}
                            <br />
                            이용자 {contract?.memberTotCount || 0} / {contract?.memberMaxNums || 0}명
                          </p>
                          <button type="button" onClick={() => navigate(`/court/mypage/contracts/${contract?.contractId}`)} />
                        </div>
                      </>
                    )}
                    {/* S2 타입: 이용일자, 이용 시간 표시 */}
                    {contract?.spaceProductType === "TIME_COURT" && (
                      <div className="contents-card__info-area">
                        {/* S2 타입: 이용일자, 이용 시간 표시 */}
                        <p className="pre-formatted">
                          {contract?.contractStartTime && contract?.contractEndTime && formatDateTimeRange(contract?.contractStartTime, contract?.contractEndTime, "S2_MAIN")}
                        </p>
                        <button type="button" onClick={() => navigate(`/court/mypage/contracts/${contract?.contractId}`)} />
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="contents-card-none">
                  <span>신청/계약 내역이 없어요.</span>
                </div>
              )}
            </div>
          )}
        </article>

        <MyPageVisitorApproval contracts={visitorApprovalContracts} />

        <BottomLinkList />
        {getAppInfo().appType === "Taap" && <Footer />}
      </div>
    </>
  );
};
export default MyPageMain;
